import React, { useEffect, useMemo } from "react";
import { FormControl, FormLabel, Grid, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import CloseIcon from "@material-ui/icons/Close";
import { useDispatch, useSelector } from "react-redux";

import { useForm } from "hooks/useForm";
import { useSearch } from "hooks/useSearch";

import RegularButton from "components/CustomButtons/Button";

import {
  getCustomersOpt,
  registerNewTaxi,
  editTaxiCustomer,
} from "redux/actions/taxis/taxisActions";

import { validateObject } from "helpers/validateObject";
import { extractIDs } from "helpers/extractIDs";
import { fieldValidations, initialValues } from "./inputValues";
import { customersOptSelector } from "./selectors";
import SearchIcon from "@material-ui/icons/Search";
import { getNfcTags } from "redux/actions/nfcTags/nfcTagsAction";
import { AutocompleteComponent } from "components/Autocomplete/AutocompleteComponent";
import { useRevalidateFetch } from "hooks/useRevalidateFetch";
import { TAXIS_STATES } from "helpers/dictionary";

export const TaxiForm = ({ isEdit, data, onClose }) => {
  const dispatch = useDispatch();
  const {
    customerOpt,
  } = useSelector(({ taxis }) => taxis);
  const customersOpt = useSelector(customersOptSelector);
  const { stations, companies } = useSelector(({ companies }) => companies);
  const { isLoadingComp } = useSelector(({ search }) => search);
  const { nfcTags } = useSelector(({ nfcTags }) => nfcTags);
  const { unitsMap } = useSelector(({ unitsReducer }) => unitsReducer);
  const { setSnackbar } = useSearch();

  const {
    inputValues,
    handleInputOnChange,
    handleNewValues,
    patchValues,
    fieldErrors,
    runValidates,
  } = useForm(initialValues, fieldValidations);

  const {
    customerSearch,
    customerSelected,
    plates,
    initialMileage,
    registrationDate,
    idTag,
    stationsOpt,
    stationsSelected,
    unitsOpt,
    unitsSelected,
  } = inputValues;

  const handleSearchCustomer = (e) => {
    e.preventDefault();
    dispatch(getCustomersOpt(customerSearch));
  };

  const handleCustomer = (e, v) => {
    const value = v ? v : null;
    if (validateObject(value)) {
      const stationsIDs = extractIDs(value.accounts, "idStation", true);
      const stationsOpt = stationsIDs.map((id) => {
        const station = stations[id];
        const companyName = companies[station.id_company].name;
        return {
          ...station,
          label2: `${companyName} - ${station.label}`,
        };
      });
      const units = unitsMap.filter(
        (unit) =>
          stationsIDs.includes(unit.idStation) && unit.type === "ESTACION"
      );
      handleNewValues({
        customerSelected: value,
        stationsOpt,
        stationsSelected: stationsOpt,
        unitsOpt: units,
        unitsSelected: units,
      });
    } else {
      handleNewValues({
        customerSelected: value,
        stationsOpt: [],
        stationsSelected: [],
        unitsOpt: [],
        unitsSelected: [],
      });
    }
  };

  const handleStations = (e, v) => {
    const value = v ? v : [];
    if (value.length) {
      const stationsIDs = extractIDs(value, "id", true);

      const units = unitsMap.filter(
        (unit) =>
          stationsIDs.includes(unit.idStation) && unit.type === "ESTACION"
      );
      handleNewValues({ stationsSelected: value, unitsOpt: units });
    } else {
      handleNewValues({ stationsSelected: value, unitsSelected: [], unitsOpt: [] });
    }
  };
  
  const handleUnits = (e, v) => {
    const value = v ? v : [];
    handleNewValues({ unitsSelected: value });
  };

  useRevalidateFetch(getNfcTags, nfcTags);

  // Solo se puede asignar una etiqueta libre. Se conserva la que el taxi ya
  // tiene (status TAXI) para que siga apareciendo seleccionada al editar.
  const assignableTags = useMemo(
    () =>
      nfcTags.filter(
        (tg) =>
          tg.status === TAXIS_STATES.FREE.name || tg.id === data?.nfcTag?.id
      ),
    [nfcTags, data]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (runValidates() || unitsSelected.length === 0)
      return setSnackbar("No pueden ir campos vacios");
    let form = {};
    if (isEdit) {
      if (parseInt(initialMileage) < parseInt(initialMileage)) {
        return setSnackbar(
          "El kilometraje inicial no puede ser menor al actual"
        );
      }
      // Los campos con prefijo `_` existen solo para excluirlos de `newForm`:
      // son estado de UI que no debe viajar al backend.
      const {
        customerSearch: _customerSearch,
        customerSelected,
        stationsSelected: _stationsSelected,
        unitsSelected,
        stationsOpt: _stationsOpt,
        taxi: _taxi,
        units: _units,
        unitsOpt: _unitsOpt,
        customer: _customer,
        stations: _stations,
        stationLabel: _stationLabel,
        idTag,
        ...newForm
      } = inputValues;
      form = {
        ...newForm,
        name: customerOpt?.name,
        idCustomer: customerSelected.id,
        stations: unitsSelected.map((unit) => {
          return {
            idUnit: unit.id,
            idStation: unit.idStation,
          };
        }),
        ...(idTag?.id && { idTag: idTag.id }),
      };
      const { name: _name, customer: _customer2, ...rest } = form;
      dispatch(editTaxiCustomer(rest, onClose));
      return;
    } 

    form = {
      idCustomer: customerSelected.id,
      plates,
      initialMileage: parseInt(initialMileage),
      registrationDate,
      number: "0",
      stations: unitsSelected.map((unit) => {
        return {
          idUnit: unit.id,
          idStation: unit.idStation,
        };
      }),
      ...(idTag?.id && { idTag: idTag.id }),
    };
    dispatch(registerNewTaxi(form, onClose));
  };

  useEffect(() => {
    if(isEdit) {
      const stationsIDs = extractIDs(data.stations, "id", true);
      const units = unitsMap.filter((unit) =>
        stationsIDs.includes(unit.idStation) && unit.type === "ESTACION"
      );

      handleNewValues({
        ...data,
        customerSelected: data.customer,
        stationsOpt: data.stations,
        stationsSelected: data.stations,
        unitsOpt: units,
        unitsSelected: data.units,
        idTag: data.nfcTag ? nfcTags.find(tg => tg.id === data.nfcTag.id) : null,
      });
    }
  }, []);

  // `nfcTags` no esta persistido en redux, asi que al montar suele venir vacio y el
  // `find` de la precarga no encuentra nada. Cuando `useRevalidateFetch` termina de
  // traer el catalogo resolvemos solo el tag asignado: el resto de la inicializacion
  // de edicion no debe re-aplicarse para no pisar lo que el usuario ya edito.
  useEffect(() => {
    if (!isEdit || !data?.nfcTag || idTag) return;
    const tag = nfcTags.find((tg) => tg.id === data.nfcTag.id);
    if (tag) patchValues({ idTag: tag });
  }, [nfcTags]);

  return (
    <FormControl>
      <FormLabel></FormLabel>
      <Grid container spacing={1}>
        <Grid item xs={11} style={{ position: "relative" }}>
          <form onSubmit={handleSearchCustomer}>
            <Autocomplete
              id="combo-box-demo"
              options={customersOpt}
              value={customerSelected}
              getOptionLabel={(customer) => customer.label || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar Cuenta"
                  name="customerSearch"
                  onChange={handleInputOnChange}
                  variant="outlined"
                  // helperText="El nombre debe de contener al menos 5 caracteres"
                />
              )}
              fullWidth
              onChange={(e, v) => handleCustomer(e, v)}
              noOptionsText={
                isLoadingComp?.loadingTaxiAccounts
                  ? "Cargando..."
                  : "No hay resultados"
              }
              closeIcon={<CloseIcon fontSize="small" />}
            />
          </form>
        </Grid>
        <Grid item xs={1} style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <RegularButton
            type="submit"
            color="info"
            size="sm"
            justIcon
            round
            onClick={handleSearchCustomer}
            disabled={isLoadingComp?.loadingTaxiAccounts}
          >
            <SearchIcon />
          </RegularButton>
        </Grid>
        <>
          <Grid item xs={12}>
            <Autocomplete
              id="combo-box-demo"
              options={stationsOpt}
              value={stationsSelected}
              getOptionLabel={(station) => station.label2 || ""}
              renderInput={(params) => (
                <TextField {...params} label="Planta" variant="outlined" />
              )}
              fullWidth
              limitTags={5}
              onChange={(e, v) => handleStations(e, v)}
              disableCloseOnSelect={true}
              multiple
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              id="combo-box-demo"
              options={unitsOpt}
              getOptionLabel={(unit) => unit.label || ""}
              value={unitsSelected}
              renderInput={(params) => (
                <TextField {...params} label="Estaciones" variant="outlined" />
              )}
              fullWidth
              limitTags={2}
              onChange={(e, v) => handleUnits(e, v)}
              disableCloseOnSelect={true}
              multiple
            />
          </Grid>
        </>
        <Grid item xs={12}>
          <AutocompleteComponent
            name="idTag"
            search="idTag"
            label="Etiqueta"
            options={assignableTags}
            value={idTag}
            handleValues={handleNewValues}
            loading={isLoadingComp?.loadingNfcTags}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Placa"
            name="plates"
            value={plates}
            variant="outlined"
            fullWidth
            required={true}
            error={fieldErrors.plates}
            helperText={fieldErrors.plates || ""}
            onChange={handleInputOnChange}
            inputProps={{
              maxLength: 9,
            }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            label="Km/Ingreso"
            name="initialMileage"
            value={initialMileage}
            variant="outlined"
            fullWidth
            required={true}
            error={fieldErrors.initialMileage}
            helperText={fieldErrors.initialMileage || ""}
            onChange={handleInputOnChange}
            inputProps={{ maxLength: 7 }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            label="Fecha Ingreso"
            name="registrationDate"
            value={registrationDate}
            variant="outlined"
            fullWidth
            disabled={true}
          />
        </Grid>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 4,
          }}
        >
          <RegularButton color="danger" tooltip="Cancelar" onClick={onClose}>
            Cancelar
          </RegularButton>
          <RegularButton color="info" tooltip="Aceptar" onClick={handleSubmit}>
            Aceptar
          </RegularButton>
        </Grid>
      </Grid>
    </FormControl>
  );
};
